import React from 'react';
import { Image } from 'antd';
import { NavLink } from 'react-router-dom';
import { CustomColumnType } from '../../components/common/table/CustomTable.types';
import { toShortLocalDate } from '../../helpers/dateHelper';
import { LiveGuidelineRecentlyModified } from '../../redux/actions/liveGuidelineActions/types';
import { Text } from '../../styles/common.styles';
import { grayDark03 } from '../../styles/colors';
import {
    DashboardProjectName,
    DashboardTableTextContainer,
    LiveGuidelineNotificationLink,
} from './Dashboard.styles';

const toTimestamp = (date?: string) => Date.parse(date ?? '') || 0;

const formatActivityLine = (count: number, action: 'added' | 'removed' | 'hidden' | 'restored') =>
    `${count} product${count === 1 ? '' : 's'} ${action}`;

export const liveGuidelineColumns = (): CustomColumnType<LiveGuidelineRecentlyModified>[] => [
    {
        title: 'LIVE GUIDELINE',
        dataIndex: 'name',
        render: (value: string, record: LiveGuidelineRecentlyModified) => (
            <NavLink to={`/live-guidelines/${record.liveGuidelineId}`}>
                <DashboardProjectName className="bold">{value}</DashboardProjectName>
            </NavLink>
        ),
    },
    {
        title: 'LAST MODIFIED',
        dataIndex: 'date',
        sorter: (a: LiveGuidelineRecentlyModified, b: LiveGuidelineRecentlyModified) =>
            toTimestamp(a.date) - toTimestamp(b.date),
        sortDirections: ['ascend', 'descend'],
        defaultSortOrder: 'descend',
        render: (value: string) => (
            <DashboardTableTextContainer>
                <Text weight={400} size={12} height={16} color={grayDark03}>
                    {value ? toShortLocalDate(value) : '-'}
                </Text>
            </DashboardTableTextContainer>
        ),
    },
    {
        title: 'ACTIVITY NOTIFICATIONS',
        render: (_value: unknown, record: LiveGuidelineRecentlyModified) => {
            const { updates } = record;
            const lines = [
                updates.added ? formatActivityLine(updates.added, 'added') : null,
                updates.removed ? formatActivityLine(updates.removed, 'removed') : null,
                updates.hidden ? formatActivityLine(updates.hidden, 'hidden') : null,
                updates.restored ? formatActivityLine(updates.restored, 'restored') : null,
            ].filter((line): line is string => Boolean(line));

            return (
                <DashboardTableTextContainer>
                    {lines.length ? (
                        lines.map((line) => (
                            <Text key={line} weight={400} size={12} height={16} color={grayDark03}>
                                {line}
                            </Text>
                        ))
                    ) : (
                        <Text weight={400} size={12} height={16} color={grayDark03}>
                            No recent activity
                        </Text>
                    )}
                </DashboardTableTextContainer>
            );
        },
    },
    {
        render: (_value: unknown, record: LiveGuidelineRecentlyModified) => (
            <LiveGuidelineNotificationLink to={`/live-guidelines/${record.liveGuidelineId}/4`}>
                See all <Image src="src/assets/images/bell.svg" width={16} preview={false} alt="see all" />
            </LiveGuidelineNotificationLink>
        ),
        width: 20,
    },
];
